import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { UserRole } from '@/shared/config/platform-config';

const ROLE_PRIORITY: UserRole[] = ['admin', 'ops', 'bod_readonly'];

/**
 * Resolves the highest-priority platform role for the current authenticated user.
 * Checks roles in priority order: admin > ops > bod_readonly.
 * Agent role is handled separately via the Agent Portal.
 */
export function useUserRole() {
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const resolveRole = async (userId: string) => {
      try {
        for (const r of ROLE_PRIORITY) {
          const { data } = await supabase.rpc('has_role', { _user_id: userId, _role: r });
          if (!isMounted) return;
          if (data === true) {
            setRole(r);
            return;
          }
        }
        // No admin-portal role found
        if (isMounted) setRole(null);
      } catch {
        if (isMounted) setRole(null);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_, session) => {
        if (!isMounted) return;
        if (session?.user) {
          setTimeout(() => resolveRole(session.user.id), 0);
        } else {
          setRole(null);
        }
      }
    );

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        if (session?.user) {
          await resolveRole(session.user.id);
        }
      } catch {
        // ignore
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    init();

    // Re-resolve roles when the window regains focus so role changes apply without re-login.
    const onFocus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (isMounted && session?.user) resolveRole(session.user.id);
    };
    window.addEventListener('focus', onFocus);

    return () => { isMounted = false; subscription.unsubscribe(); window.removeEventListener('focus', onFocus); };
  }, []);

  return { role, loading };
}
